import React, { useState, useRef, useEffect } from "react";
import api from "../../api";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
    Send, Sparkles, MessageSquarePlus, Bug, Lightbulb, RotateCcw, Bot, User as UserIcon, Loader2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Slide-out Help-Panel mit:
 * - AI-Chat (Claude antwortet aus Wissensbasis)
 * - "Tutorial wiederholen"-Button
 * - "Case öffnen"-Form (wenn AI nicht weiß)
 */
export default function HelpPanel({ open, onOpenChange, onReplayTutorial }) {
    const [mode, setMode] = useState("chat"); // chat | case
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content: "Hi! Ich bin dein MyTACS-Hilfe-Assistent. Stell mir eine Frage — z.B. 'Wo lege ich einen Mitarbeiter an?' oder 'Wie aktiviere ich 2FA?'",
            suggest_case: false,
        },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [lastFailedQuestion, setLastFailedQuestion] = useState("");
    const scrollRef = useRef(null);

    // Case form state
    const [caseType, setCaseType] = useState("question");
    const [caseSubject, setCaseSubject] = useState("");
    const [caseDescription, setCaseDescription] = useState("");
    const [caseSubmitting, setCaseSubmitting] = useState(false);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, loading]);

    const send = async () => {
        const q = input.trim();
        if (!q || loading) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: q }]);
        setLoading(true);
        try {
            const { data } = await api.post("/help/ask", { question: q, locale: "de" });
            setMessages((m) => [...m, { role: "assistant", content: data.answer, suggest_case: data.suggest_case }]);
            if (data.suggest_case) setLastFailedQuestion(q);
        } catch (e) {
            setMessages((m) => [...m, {
                role: "assistant",
                content: "Hoppla, da ist gerade was schiefgelaufen. Versuche es nochmal oder öffne einen Case.",
                suggest_case: true,
            }]);
        } finally {
            setLoading(false);
        }
    };

    const openCaseForQuestion = (q) => {
        setMode("case");
        setCaseType("question");
        setCaseSubject(q.slice(0, 80));
        setCaseDescription(`Frage aus dem Hilfe-Chat: ${q}`);
    };

    const submitCase = async () => {
        if (!caseSubject.trim() || !caseDescription.trim()) {
            toast.error("Betreff und Beschreibung sind Pflicht");
            return;
        }
        setCaseSubmitting(true);
        try {
            await api.post("/cases", {
                type: caseType,
                subject: caseSubject.trim(),
                description: caseDescription.trim(),
                related_question: lastFailedQuestion || undefined,
            });
            toast.success("Case erstellt — das Team meldet sich bei dir");
            setCaseSubject(""); setCaseDescription(""); setLastFailedQuestion("");
            setMode("chat");
            setMessages((m) => [...m, {
                role: "assistant",
                content: "✅ Dein Case wurde erstellt. Sobald jemand sich darum kümmert, bekommst du eine Antwort.",
                suggest_case: false,
            }]);
        } catch (e) {
            toast.error("Case konnte nicht erstellt werden");
        } finally {
            setCaseSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="help-panel">
                <SheetHeader className="px-5 py-4 border-b border-neutral-200">
                    <SheetTitle className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-neutral-700" />
                        Hilfe & Support
                    </SheetTitle>
                    <SheetDescription className="text-xs">
                        Ich kenne MyTACS auswendig. Was kann ich dir zeigen?
                    </SheetDescription>
                </SheetHeader>

                {/* Mode toggle + Tutorial-Replay */}
                <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between gap-2">
                    <div className="inline-flex rounded-md border border-neutral-200 overflow-hidden text-xs">
                        <button
                            onClick={() => setMode("chat")}
                            className={`px-3 py-1.5 flex items-center gap-1.5 ${mode === "chat" ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"}`}
                            data-testid="help-mode-chat"
                        >
                            <Bot className="w-3.5 h-3.5" /> KI-Chat
                        </button>
                        <button
                            onClick={() => setMode("case")}
                            className={`px-3 py-1.5 flex items-center gap-1.5 border-l border-neutral-200 ${mode === "case" ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"}`}
                            data-testid="help-mode-case"
                        >
                            <MessageSquarePlus className="w-3.5 h-3.5" /> Case
                        </button>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { onReplayTutorial && onReplayTutorial(); onOpenChange(false); }}
                        data-testid="help-replay-tutorial"
                        className="text-xs"
                    >
                        <RotateCcw className="w-3 h-3 mr-1" /> Tutorial
                    </Button>
                </div>

                {/* === KI-CHAT === */}
                {mode === "chat" && (
                    <>
                        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="help-chat-thread">
                            {messages.map((m, i) => (
                                <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                                    <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
                                        m.role === "user" ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-700"
                                    }`}>
                                        {m.role === "user" ? <UserIcon className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                                    </div>
                                    <div className={`flex-1 ${m.role === "user" ? "text-right" : ""}`}>
                                        <div className={`inline-block rounded-lg px-3 py-2 text-sm whitespace-pre-wrap text-left ${
                                            m.role === "user"
                                                ? "bg-neutral-900 text-white"
                                                : "bg-neutral-50 border border-neutral-200 text-neutral-800"
                                        }`} style={{ maxWidth: "92%" }}>
                                            {m.content}
                                        </div>
                                        {m.suggest_case && (
                                            <div className="mt-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => openCaseForQuestion(lastFailedQuestion || "")}
                                                    className="text-xs"
                                                    data-testid="help-open-case-btn"
                                                >
                                                    <MessageSquarePlus className="w-3 h-3 mr-1" />
                                                    Case dafür öffnen
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="flex gap-2 text-sm text-neutral-500 items-center">
                                    <div className="w-7 h-7 rounded-full bg-neutral-100 flex items-center justify-center">
                                        <Bot className="w-3.5 h-3.5" />
                                    </div>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Überlege ...
                                </div>
                            )}
                        </div>

                        {/* Input row */}
                        <div className="border-t border-neutral-200 p-3 flex gap-2">
                            <Input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                                placeholder="z.B. Wo lege ich Mitarbeiter an?"
                                disabled={loading}
                                data-testid="help-chat-input"
                            />
                            <Button onClick={send} disabled={loading || !input.trim()} className="bg-neutral-900 hover:bg-neutral-800" data-testid="help-chat-send">
                                <Send className="w-4 h-4" />
                            </Button>
                        </div>
                    </>
                )}

                {/* === CASE FORM === */}
                {mode === "case" && (
                    <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="help-case-form">
                        <div className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-md p-3">
                            Dein Case landet beim Owner-Team und wird so schnell wie möglich beantwortet — oft per neuem Feature.
                        </div>
                        <div>
                            <Label className="mb-1.5 block">Typ</Label>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { v: "question", label: "Frage", icon: Sparkles },
                                    { v: "bug",      label: "Bug",   icon: Bug },
                                    { v: "feature",  label: "Wunsch", icon: Lightbulb },
                                ].map(({ v, label, icon: I }) => (
                                    <button
                                        key={v}
                                        onClick={() => setCaseType(v)}
                                        className={`p-2 rounded-md border text-xs flex flex-col items-center gap-1 ${
                                            caseType === v
                                                ? "bg-neutral-900 text-white border-neutral-900"
                                                : "bg-white border-neutral-200 hover:bg-neutral-50"
                                        }`}
                                        data-testid={`help-case-type-${v}`}
                                    >
                                        <I className="w-4 h-4" />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="mb-1.5 block">Betreff</Label>
                            <Input
                                value={caseSubject}
                                onChange={(e) => setCaseSubject(e.target.value)}
                                placeholder="z.B. SAP-Integration möglich?"
                                data-testid="help-case-subject"
                            />
                        </div>
                        <div>
                            <Label className="mb-1.5 block">Beschreibung</Label>
                            <Textarea
                                rows={6}
                                value={caseDescription}
                                onChange={(e) => setCaseDescription(e.target.value)}
                                placeholder="Beschreibe, was du brauchst oder was nicht funktioniert. Je präziser, desto schneller die Antwort."
                                data-testid="help-case-description"
                            />
                        </div>
                        <Button
                            onClick={submitCase}
                            disabled={caseSubmitting}
                            className="w-full bg-neutral-900 hover:bg-neutral-800"
                            data-testid="help-case-submit"
                        >
                            {caseSubmitting ? "Sende ..." : "Case absenden"}
                        </Button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
